import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { DsBadge, DsButton, DsSpinner } from "../../../../../design-system";
import { attendanceService } from "../../../../../services/api";
import {
  formatCompOffDate,
  formatCompOffDays,
  formatCompOffSource,
  formatCompOffStatus,
  summarizeCompOffLedger,
} from "../../../../../lib/compOffLedger";
import {
  PHONE_SHEET_BACKDROP_CLS,
  PHONE_SHEET_PANEL_CLS,
  usePhoneFormBack,
} from "../RequestHistoryOverlay";

function employeeLabel(employee) {
  if (!employee) return "Employee";
  const code = employee.empCode || employee.employeeCode || employee.code || "";
  const name =
    employee.user?.name ||
    employee.employeeName ||
    employee.name ||
    employee.fullName ||
    [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
  if (code && name) return `${code} — ${name}`;
  return name || code || "Employee";
}

function statusTone(row) {
  const status = String(row?.status || "");
  if (row?.expired && status === "credited") return "warning";
  if (status === "credited") return "success";
  if (status === "used") return "info";
  if (status === "expired") return "warning";
  if (status === "rejected" || status === "cancelled") return "danger";
  return "neutral";
}

export default function CompOffLedgerPopup({
  darkMode = false,
  isOpen,
  onClose,
  employee,
}) {
  const [rows, setRows] = useState([]);
  const [balanceDays, setBalanceDays] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !employee?.id) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [ledgerRes, balRes] = await Promise.all([
          attendanceService.listCompOffLedger({ employeeId: employee.id, limit: 2000 }),
          attendanceService.getCompOffBalance({ employeeId: employee.id }),
        ]);
        if (cancelled) return;
        const list = Array.isArray(ledgerRes?.data)
          ? ledgerRes.data
          : Array.isArray(ledgerRes)
            ? ledgerRes
            : [];
        setRows(list);
        const bal = balRes?.data?.balanceDays ?? balRes?.balanceDays;
        setBalanceDays(bal == null ? null : Number(bal));
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setRows([]);
        toast.error("Failed to load Comp Off ledger.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, employee?.id]);

  const summary = useMemo(() => summarizeCompOffLedger(rows), [rows]);
  usePhoneFormBack(isOpen, onClose);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className={`fixed inset-0 z-[11050] flex items-center justify-center bg-black/50 p-4 ${PHONE_SHEET_BACKDROP_CLS}`}>
      <div
        className={`flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)] ${PHONE_SHEET_PANEL_CLS} ${
          darkMode ? "" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Comp Off ledger"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ds-page-panel-head flex items-center justify-between">
          <div>
            <h2 className="ds-h3">Comp Off ledger</h2>
            <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
              {employeeLabel(employee)} — earned lots, availed days, and expiry
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
            aria-label="Close Comp Off ledger"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3 sm:grid-cols-4">
          <div>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Available balance</p>
            <p className="ds-h4 mt-0.5">
              {balanceDays == null ? "—" : formatCompOffDays(balanceDays)} days
            </p>
          </div>
          <div>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Earned</p>
            <p className="ds-body mt-0.5 font-semibold">{formatCompOffDays(summary.earned)}</p>
          </div>
          <div>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Availed</p>
            <p className="ds-body mt-0.5 font-semibold">{formatCompOffDays(summary.availed)}</p>
          </div>
          <div>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">Expired</p>
            <p className="ds-body mt-0.5 font-semibold">{formatCompOffDays(summary.expired)}</p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <DsSpinner />
            </div>
          ) : rows.length === 0 ? (
            <p className="ds-body py-12 text-center text-[hsl(var(--ds-muted-fg))]">
              No Comp Off earn or avail records yet.
            </p>
          ) : (
            <>
            <ul className="space-y-2 md:hidden">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="rounded-xl border border-[hsl(var(--ds-border))] p-3 text-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold">
                      {row.movement === "availed" ? "Availed" : "Earned"}
                    </p>
                    <DsBadge tone={statusTone(row)}>{formatCompOffStatus(row)}</DsBadge>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    {[
                      ["Worked date", formatCompOffDate(row.sourceDate)],
                      ["Source", formatCompOffSource(row.sourceType)],
                      ["Hours", row.hoursWorked == null ? "—" : formatCompOffDays(row.hoursWorked)],
                      ["Days", formatCompOffDays(row.creditDays)],
                      ["Expires on", formatCompOffDate(row.expiresOn)],
                      ["Availed on", formatCompOffDate(row.availDate)],
                      ["Remaining", formatCompOffDays(row.remainingDays)],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-[hsl(var(--ds-muted-fg))]">{label}</dt>
                        <dd className="font-medium">{value}</dd>
                      </div>
                    ))}
                  </dl>
                  {row.notes ? (
                    <p className="mt-2 text-xs text-[hsl(var(--ds-muted-fg))]">{row.notes}</p>
                  ) : null}
                </li>
              ))}
            </ul>
            <table className="ds-table w-full text-left text-sm max-md:hidden">
              <thead className="sticky top-0 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Worked date</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                  <th className="px-3 py-2 font-semibold text-right">Hours</th>
                  <th className="px-3 py-2 font-semibold text-right">Days</th>
                  <th className="px-3 py-2 font-semibold">Expires on</th>
                  <th className="px-3 py-2 font-semibold">Availed on</th>
                  <th className="px-3 py-2 font-semibold text-right">Remaining</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-[hsl(var(--ds-border))]"
                  >
                    <td className="px-3 py-2 capitalize">
                      {row.movement === "availed" ? "Availed" : "Earned"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatCompOffDate(row.sourceDate)}
                    </td>
                    <td className="px-3 py-2">{formatCompOffSource(row.sourceType)}</td>
                    <td className="px-3 py-2 text-right">
                      {row.hoursWorked == null ? "—" : formatCompOffDays(row.hoursWorked)}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatCompOffDays(row.creditDays)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatCompOffDate(row.expiresOn)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatCompOffDate(row.availDate)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {formatCompOffDays(row.remainingDays)}
                    </td>
                    <td className="px-3 py-2">
                      <DsBadge tone={statusTone(row)}>{formatCompOffStatus(row)}</DsBadge>
                    </td>
                    <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">
                      {row.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>

        <div className="flex justify-end border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="outline" onClick={onClose} className="max-md:min-h-[44px] max-md:w-full">

            Close
          </DsButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
